import { useEffect, useState } from "react";
import { Send, MailCheck, Hourglass } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "../lib/api";
import { Field, Loader } from "./kit";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { EmailPreview } from "./InviteEmailEditor";

function Fields({ tpl, setTpl }) {
  const set = (k) => (e) => setTpl({ ...tpl, [k]: e.target.value });
  return (
    <div className="space-y-3">
      <Field label="Subject"><input className="input" maxLength={150} value={tpl.subject} onChange={set("subject")} data-testid="last-chance-subject" /></Field>
      <Field label="Message" hint="Tags like {first_name}, {company}, {months}, {plan} and {expiry_date} are filled in per provider.">
        <textarea className="input min-h-[200px] text-sm leading-relaxed" maxLength={3000} value={tpl.message} onChange={set("message")} data-testid="last-chance-message" />
      </Field>
      <Field label="Button text"><input className="input" maxLength={40} value={tpl.button} onChange={set("button")} data-testid="last-chance-button" /></Field>
    </div>
  );
}

export function LastChanceDialog({ batchId, onClose, onSent }) {
  const [info, setInfo] = useState(null);
  const [tpl, setTpl] = useState(null);
  const [busy, setBusy] = useState("");
  useEffect(() => {
    if (!batchId) return;
    setInfo(null);
    api.get(`/admin/invite-batches/${batchId}/last-chance`).then((r) => { setInfo(r.data); setTpl(r.data.template); }).catch((e) => { toast.error(errMsg(e)); onClose(); });
  }, [batchId]); // eslint-disable-line
  const b = info?.batch;
  const payload = b && { plan: b.plan, months: b.months, source: b.source, template: tpl, last_chance: true };
  const run = async (name, fn) => { setBusy(name); try { await fn(); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(""); } };
  const test = () => run("test", async () => { const { data } = await api.post("/admin/invites/test", payload); toast.success(`Test email sent to ${data.to}`); });
  const send = () => run("send", async () => {
    if (!window.confirm(`Send the last-chance email to ${info.eligible} providers? Each provider only ever gets this once.`)) return;
    const { data } = await api.post(`/admin/invite-batches/${batchId}/last-chance`, { template: tpl });
    toast.success(`Last-chance email sent to ${data.sent} providers`); onSent(); onClose();
  });
  return (
    <Dialog open={!!batchId} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-5xl max-h-[92vh] overflow-y-auto" data-testid="last-chance-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Hourglass size={18} className="text-[#FF8166]" />Last-chance email{b ? `: ${b.name}` : ""}</DialogTitle>
          <DialogDescription>Goes once to everyone in this batch who hasn't joined yet. Their link is kept open for at least {info?.days || 7} more days, including expired links.</DialogDescription>
        </DialogHeader>
        {!info || !tpl ? <Loader /> : (
          <>
            <p className="text-sm" data-testid="last-chance-counts"><b>{info.eligible}</b> providers will get it{info.already_sent > 0 && <span className="muted"> · {info.already_sent} already had one</span>}</p>
            <div className="grid lg:grid-cols-2 gap-5">
              <Fields tpl={tpl} setTpl={setTpl} />
              <EmailPreview payload={payload} ready />
            </div>
            <div className="flex flex-wrap gap-2 justify-end">
              <button onClick={test} disabled={!!busy} className="btn-ghost btn-sm" data-testid="last-chance-test-btn"><MailCheck size={14} />{busy === "test" ? "Sending…" : "Send test to me"}</button>
              <button onClick={send} disabled={!!busy || !info.eligible} className="btn-coral" data-testid="last-chance-send-btn"><Send size={16} />{busy === "send" ? "Sending…" : `Send to ${info.eligible} providers`}</button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
